<template>
    <pendo-card class="import-feedback-drawer-guide-review-step__card">
        <div class="import-feedback-drawer-guide-review-step__header">
            <img
                class="import-feedback-drawer-guide-review-step__header-image"
                src="@img/voc/NPS-import-review.svg"
                width="131"
                height="92">
            <h3>{{ reviewStepTitle }}</h3>
            <import-feedback-drawer-guide-bar-card
                :guide="guide"
                :type="guideType" />
        </div>
        <div
            v-if="reviewInfoText"
            class="import-feedback-drawer-guide-review-step__info">
            <pendo-icon
                type="info-solid"
                size="24"
                stroke="var(--palette-gray-0)"
                class="import-feedback-drawer-guide-review-step__info-icon" />
            <p class="import-feedback-drawer-guide-review-step__info-text">
                {{ reviewInfoText }}
            </p>
        </div>
        <div
            v-if="savedViewCreated"
            class="import-feedback-drawer-guide-review-step__new-view">
            <h4 class="import-feedback-drawer-guide-review-step__new-view-title">
                New feedback view created
            </h4>
            <p class="import-feedback-drawer-guide-review-step__new-view-subtitle">
                {{ responsesText }}
            </p>
            <pendo-button
                label="Take me to the feedback view"
                theme="app"
                type="subtle"
                prefix-icon="external-link"
                @click="$emit('view-insights-new-tab')" />
        </div>
        <div
            v-if="historicImport && totalItems"
            class="import-feedback-drawer-guide-review-step__stat-wrapper">
            <pendo-divider />
            <p
                v-if="isNPS"
                class="import-feedback-drawer-guide-review-step__timeframe">
                Import results for responses {{ formatTimeframe }}
            </p>
            <div class="import-feedback-drawer-guide-review-step__footer">
                <div class="import-feedback-drawer-guide-review-step__stat-section">
                    <div class="import-feedback-drawer-guide-review-step__stat-percentage-section">
                        <img
                            class="import-feedback-drawer-guide-review-step__stat-percentage-image"
                            src="@img/voc/successful-uploads.svg"
                            width="40"
                            height="40">
                        <p class="import-feedback-drawer-guide-review-step__stat-percentage">
                            {{ successfulPercentage }}%
                        </p>
                    </div>
                    <p class="import-feedback-drawer-guide-review-step__stat-uploads">
                        Successful
                    </p>
                    <p class="import-feedback-drawer-guide-review-step__stat-upload-stat">
                        ({{ successfulStat }})
                    </p>
                </div>
                <div class="import-feedback-drawer-guide-review-step__stat-section">
                    <div class="import-feedback-drawer-guide-review-step__stat-percentage-section">
                        <img
                            class="import-feedback-drawer-guide-review-step__stat-percentage-image"
                            src="@img/voc/failed-uploads.svg"
                            width="40"
                            height="40">
                        <p class="import-feedback-drawer-guide-review-step__stat-percentage">
                            {{ failedPercentage }}%
                        </p>
                    </div>
                    <p class="import-feedback-drawer-guide-review-step__stat-uploads">
                        Unsuccessful
                    </p>
                    <p class="import-feedback-drawer-guide-review-step__stat-upload-stat">
                        ({{ failedStat }})
                    </p>
                    <pendo-button
                        v-if="failedItems > 0"
                        theme="app"
                        type="subtle"
                        label="Download error CSV"
                        @click="$emit('generate-csv-errors')" />
                </div>
            </div>
        </div>
    </pendo-card>
</template>

<script>
import { PendoCard, PendoButton, PendoIcon, PendoDivider } from '@pendo/components';
import moment from '@utils/moment-wrapper';
import ImportFeedbackDrawerGuideBarCard from '@/voc/components/drawers/import-feedback-drawer/components/import-feedback-drawer-guide-bar-card/import-feedback-drawer-guide-bar-card.vue';

export default {
    name: 'ImportFeedbackDrawerGuideReviewStep',
    components: {
        PendoCard,
        PendoButton,
        PendoIcon,
        PendoDivider,
        ImportFeedbackDrawerGuideBarCard,
    },
    props: {
        guide: {
            type: [Object, null],
            required: true,
        },
        historicImport: {
            type: Boolean,
            required: true,
        },
        automaticImport: {
            type: Boolean,
            required: true,
        },
        totalItems: {
            type: Number,
            required: true,
        },
        timeframe: {
            type: [Object, null],
            default: () => null,
        },
        savedViewCreated: {
            type: Boolean,
            required: true,
        },
        successfulPercentage: {
            type: Number,
            required: true,
        },
        successfulStat: {
            type: String,
            required: true,
        },
        failedPercentage: {
            type: Number,
            required: true,
        },
        failedStat: {
            type: String,
            required: true,
        },
        failedItems: {
            type: Number,
            required: true,
        },
        reviewInfoText: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
    },
    emits: ['generate-csv-errors', 'view-insights-new-tab'],
    computed: {
        reviewStepTitle () {
            const source = this.type === 'NPS' ? 'NPS' : 'Guide poll';

            if (this.historicImport && this.totalItems && this.automaticImport) {
                return `${source} connected to Listen and import complete`;
            }
            if (this.historicImport && this.totalItems) {
                return `${source} import complete`;
            }
            if (this.automaticImport) {
                return `${source} connected to Listen`;
            }

            return `${source} settings updated`;
        },
        formatTimeframe () {
            if (this.timeframe.label.includes('Last')) {
                const content = this.timeframe.label
                    .replace('(including today)', '')
                    .trim()
                    .toLowerCase();

                return `over the ${content}`;
            }
            if (this.timeframe.label.includes('Custom')) {
                const startDate = moment(this.timeframe.start).format('MMM D, YYYY');
                const endDate = moment(this.timeframe.end).format('MMM D, YYYY');

                return `from ${startDate} - ${endDate}`;
            }

            return 'for all time';
        },
        responsesText () {
            const subText = this.type === 'NPS' ? 'NPS' : 'poll';

            return `We've created a dedicated view for these ${subText} responses in Listen.`;
        },
        isNPS () {
            return this.type === 'NPS';
        },
        guideType () {
            return this.type === 'guide' ? 'Guide' : 'NPS';
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/voc/styles/voc.style.less';

.import-feedback-drawer-guide-review-step {
    .pendo-card {
        :deep(.pendo-card__body) {
            padding: 32px;
        }
    }

    &__card {
        display: flex;
        flex-direction: column;
        width: 700px;
        margin: auto;
    }

    &__header {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;

        & h3 {
            font-weight: 600;
            margin-top: 16px;
            margin-bottom: 10px;
        }

        &-subtitle {
            line-height: 150%;
        }
    }
    &__stat-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
    }
    &__footer {
        display: flex;
        width: 100%;
        align-items: flex-start;
        justify-content: center;
        text-align: center;
        grid-gap: 16px;
    }

    &__stat {
        &-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding-top: 16px;
            text-align: center;
            border-radius: 4px;
            width: 30%;
        }

        &-percentage-section {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 4px;
        }

        &-percentage-image {
            width: 40px;
            height: 40px;
            margin-right: 10px;
        }

        &-percentage {
            font-size: 28px;
            font-weight: 600;
            margin-bottom: 0;
        }

        &-uploads {
            margin-bottom: 6px;
        }

        &-upload-stat {
            margin-bottom: 10px;
            color: var(--palette-gray-70);
        }
    }
    &__info {
        margin-top: 23px;
        display: flex;
        justify-content: flex-start;
        gap: 12px;
        align-items: center;
        border: 1px solid var(--palette-teal-70);
        background: var(--palette-teal-10);
        border-radius: 3px;
        padding: 16px;
        line-height: 150%;
    }

    &__info-text {
        margin: 0;
    }

    &__info-icon {
        :deep(circle) {
            fill: var(--palette-teal-70);
        }
    }

    &__new-view {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        text-align: center;
        line-height: 150%;
        margin-top: 23px;
    }

    &__new-view-title {
        font-weight: 600;
    }

    &__timeframe {
        font-weight: 600;
        line-height: 150%;
        margin: 0 16px;
        text-align: center;
    }
}
</style>
